import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './product-element.module.scss';
import { useTranslation } from 'react-i18next';
import { useServices } from '@/services-context';
import { ImageResizer } from '@/util/image-resizer';
import { CurrencyConverter } from '@/util/currency-converter';
import FixImage from '../fix-image';
import ViewDetails from '../view-details';
import AddToCart from '../add-to-cart';
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { FORMATTER_LISTENER_NAME } from "@/services/formatter.ts";
import { testid } from "@fixparts/testid/index.ts";
import { googleEvent } from "@/helpers/google-events.ts";
import { Gtag } from "@/services/gtag.ts";
import { useCallback } from "preact/hooks";
import { ProductStateType } from "@/util/constants.ts";
import { defineOutOfStock } from "@/helpers/product-manager.ts";
import ProductStatus from "@/elements-react/product-status";

export interface ProductElementProps {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_MD;
  name: string;
  brand: string;
  mpn: string;
  productId: string;
  price: [number, number];
  discount: number;
  images: Array<{ path: string }>;
  state: number;
  originalMpn?: string;
  href?: string;
  mode?: 'cart' | 'details';
  index: number;
  isPrimaryType?: boolean;
  exchanges?: Array<any>;
  googleEventData: {
    [p: `item_category${string}`]: string | undefined;
    item_name: string;
    item_id: string;
    price: number;
    item_brand: string;
    index: number;
    quantity: string;
  } | undefined;
  stockType: string;
  isHidePriceIfProductIsOutOfStock: boolean;
  deliveryStandardTimesIsNotDefined: boolean;
}

export const ProductElement: React.FC<ProductElementProps> = ({
  type = LayoutStyleTypes.TYPE_LG,
  name,
  brand,
  mpn,
  productId,
  price,
  discount,
  images,
  href,
  mode = 'details',
  state,
  originalMpn,
  googleEventData,
  isPrimaryType = false,
  exchanges,
  stockType,
  isHidePriceIfProductIsOutOfStock,
  deliveryStandardTimesIsNotDefined,
}) => {
  const { t } = useTranslation();
  const services = useServices([FORMATTER_LISTENER_NAME]);
  const [added, setAdded] = useState(false);
  const [cursorPending, setCursorPending] = useState(false);
  const [clickPending, setClickPending] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);
  const [multiline, setMultiline] = useState(false);
  const { defaultLayoutStyleType } = useLayoutType();

  useEffect(() => {
    if (nameRef.current) {
      const realHeight = nameRef.current.clientHeight;
      const targetHeight = parseFloat(getComputedStyle(nameRef.current).getPropertyValue('line-height'));
      if (realHeight > targetHeight) {
        setMultiline(true);
      }
    }
  }, [name]);

  const getImage = () => {
    return images?.[0]?.path ? ImageResizer.getProductImageUrl(images[0].path, '160x160') : undefined;
  };

  const hasDiscount = () => (discount ?? 0) > 0;

  const getPriceText = () => {
    const from = price[0];
    const to = price[1] ?? from;
    return services.formatter.priceRange([
      CurrencyConverter.convertCents(from),
      CurrencyConverter.convertCents(to),
    ]);
  };

  const getProductStatus = () => {
    const isDiscontinued = state === ProductStateType.Discontinued && !exchanges?.length && isPrimaryType;

    if (!isDiscontinued && !originalMpn) {
      return null;
    }

    let text;

    if (originalMpn) {
      text = (
        <span className={styles["status-text"]}>
          {t("bk4fnvbncvx31kvc.replacement-for", "Replacement for")} <span className={styles["exchange-mpn"]}>{originalMpn}</span>
        </span>
      );
    } else if (isDiscontinued) {
      text = <span>{t("qwe9ieuj32nmvjib.not-produced", "Not produced")}</span>;
    }

    const className = originalMpn ? "exchange" : "inactive";

    return <div className={classNames(styles.status, styles[className])}>{text}</div>;
  };

  const getActionClassNames = () => ({
    'state-active': clickPending,
    'state-hover': !clickPending && cursorPending,
  });

  const handleCartClick = () => setAdded(true);

  const handleMouseEnter = () => setCursorPending(true);
  const handleMouseLeave = () => {
    setCursorPending(false);
    setClickPending(false);
  };
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button === 0) setClickPending(true);
  };
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button === 0) setClickPending(false);
  };

  const actionClassNames = getActionClassNames();
  const containerClass = classNames(styles.container, actionClassNames, styles[defaultLayoutStyleType], "productCardRoot", { [styles.primary]: isPrimaryType });
  const buttonClass = classNames('cart', actionClassNames);
  const nameClass = classNames(styles.name, { [styles.multiline]: multiline });

  const hasProperty = (
    type:
      | "onRequest"
      | "noPrice"
      | "outOfStock"
      | "hasPrice",
  ): boolean => {
    const hasPrice = price.length && !!price[0] && !!price[1];

    switch (type) {
      case "onRequest":
        return state === ProductStateType.OnRequest;
      case "noPrice":
        return !hasPrice;
      case "outOfStock":
        return defineOutOfStock({
          hasPrice,
          deliveryStandardTimesIsNotDefined,
          isHidePriceIfProductIsOutOfStock,
          stockType,
          productState: state,
        });
      case "hasPrice":
        return hasPrice;
      default:
        return false;
    }
  };

  const availableForOrder = () => {
    return (
      (hasProperty("hasPrice") && state === ProductStateType.Active) &&
      !hasProperty("outOfStock") &&
      !hasProperty("onRequest")
    );
  };

  const productStatus = () => {
    return hasProperty("outOfStock") ? "out-stock-not-available-for-order" : "";
  };

  const content = (
    <div
      className={containerClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...testid({ block: "productCard", modifier: mpn })}
    >
      {getProductStatus()}
      <div className={styles.image}>
        <FixImage
          className={styles["image-item"]}
          type={LayoutStyleTypes.TYPE_SM}
          src={getImage()}
          alt={`${t("asb79fiaugrbsfa8.product", "Product")} ${name}`}
        />
      </div>
      <div className={nameClass}>
        <div className={styles.mpn} {...testid({ block: "productCard", elm: "line", modifier: "mpn" })}>
          {mpn}
        </div>
        <div ref={nameRef} className={styles.productName} {...testid({ block: "productCard", elm: "line", modifier: "name" })}>
          {name}
        </div>
      </div>
      <div className={styles.brand} {...testid({ block: "productCard", elm: "line", modifier: "brand" })}>
        {brand}
      </div>
      <div className={styles.statuses}>
        {productStatus() && <ProductStatus status={productStatus()} type />}
        {hasProperty("onRequest") && <div className={styles.badge}>{t("3b91531d.onRequest", "On request")}</div>}
        {hasProperty("noPrice") && <div className={styles["request-price"]}>{t("o8s7grb7gfwviwg7.contact-to-request", "Contact us to request a price")}</div>}
      </div>
      <div className={classNames(styles.price, !availableForOrder() && styles.disabled)} {...testid({ block: "productCard", elm: "line", modifier: "price" })}>
        {hasProperty("hasPrice") && getPriceText()}
      </div>
      {mode === "cart" ? (
        <AddToCart className={buttonClass} type={LayoutStyleTypes.TYPE_SM} done={added} onClick={handleCartClick} />
      ) : (
        <ViewDetails className={buttonClass} type={LayoutStyleTypes.TYPE_SM} onClick={() => googleEvent("product_click", productId)} />
      )}
    </div>
  );

  const handleOnClick = useCallback(() => {
    googleEvent("product_click", productId);
    Gtag.pushEventEcommerce("select_item", { items: [googleEventData] });
  }, [JSON.stringify(googleEventData)]);

  return href ? (
    <a className={styles['link-wrapper']} href={href} draggable={false} data-product-id={productId} onClick={handleOnClick}>
      {content}
    </a>
  ) : (
    content
  );
};

export default ProductElement;
